import { Injectable, OnDestroy } from '@angular/core';
import { RawCompanyData } from '@company/const';
import { Store, select } from '@ngrx/store';
import { StatePersistenceService, StorageSyncType } from '@spartacus/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { CompanyActions } from './actions';
import { StateWithCompany } from './company-state';
import { CompanySelectors } from './selectors';

export interface SyncedCompanyState {
  selected?: string;
  saved?: RawCompanyData;
}

@Injectable({
  providedIn: 'root',
})
export class CompanyPersistenceService implements OnDestroy {
  private subscription = new Subscription();

  constructor(
    private statePersistenceService: StatePersistenceService,
    private store: Store<StateWithCompany>,
  ) {}

  sync(): void {
    this.subscription.add(
      this.statePersistenceService.syncWithStorage({
        storageType: StorageSyncType.LOCAL_STORAGE,
        key: 'company',
        state$: this.getState(),
        onRead: (state) => this.onRead(state),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getState(): Observable<SyncedCompanyState> {
    return combineLatest([
      this.store.pipe(select(CompanySelectors.getSelectedCompany)),
      this.store.pipe(select(CompanySelectors.getSavedCompany)),
    ]).pipe(
      map(([selected, saved]) => ({
        selected,
        saved,
      })),
    );
  }

  private onRead(state: SyncedCompanyState = {}): void {
    if (state.selected) {
      this.store.dispatch(new CompanyActions.SyncSelectedCompanyState(state.selected));
    }

    if (state.saved) {
      this.store.dispatch(new CompanyActions.SyncSavedCompanyState(state.saved));
    }
  }
}
